import {
  ArrowRight,
  BadgeCheck,
  BookOpenCheck,
  CalendarCheck2,
  Check,
  Clock3,
  GraduationCap,
  Menu,
  MessageCircleMore,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { viewUrl } from "../App";
import { api } from "../lib/api";
import type { PublicChallengeState } from "../types";
import { AboutJoeSection } from "./AboutJoeSection";
import { BookingSection } from "./BookingSection";
import { ChallengeGallery } from "./ChallengeGallery";
import { ChallengeSection } from "./ChallengeSection";
import { FaqSection } from "./FaqSection";
import { PricingSection } from "./PricingSection";
import { TestimonialsSection, type Testimonial } from "./TestimonialsSection";
import { WhatsAppCta } from "./WhatsAppCta";

// TODO: Erst mit nachweislich echten Stimmen befüllen. Ohne Einträge bleibt die Sektion unsichtbar.
const testimonials: Testimonial[] = [];

export function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [challengeState, setChallengeState] = useState<PublicChallengeState>();

  useEffect(() => {
    let active = true;
    api.publicChallenges()
      .then((state) => { if (active) setChallengeState(state); })
      .catch(() => { if (active) setChallengeState({ enabled: false, challenges: [] }); });
    return () => { active = false; };
  }, []);

  const challengeEnabled = challengeState?.enabled === true;
  const requestBookingOpen = () => window.dispatchEvent(new Event("booking:open"));

  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="brand" href="#top" aria-label="Mathe mit Joe – Startseite">
          <span className="brand-mark">J</span>
          <span><strong>Mathe mit Joe</strong><small>Persönliche Online-Nachhilfe</small></span>
        </a>
        <button
          className="menu-toggle"
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-label={menuOpen ? "Navigation schließen" : "Navigation öffnen"}
          aria-expanded={menuOpen}
          aria-controls="main-navigation"
        >
          {menuOpen ? <X /> : <Menu />}
        </button>
        <nav id="main-navigation" className={menuOpen ? "main-nav is-open" : "main-nav"} aria-label="Hauptnavigation">
          <a href="#ueber-mich" onClick={() => setMenuOpen(false)}>Über mich</a>
          <a href="#so-lernst-du" onClick={() => setMenuOpen(false)}>So lernst du</a>
          <a href="#preise" onClick={() => setMenuOpen(false)}>Preise</a>
          {challengeEnabled && <a href="#challenge" onClick={() => setMenuOpen(false)}>Challenge me</a>}
          <a href="#ablauf" onClick={() => setMenuOpen(false)}>So läuft die Probestunde ab</a>
          <a className="nav-cta" href="#booking" onClick={() => setMenuOpen(false)}>
            Kostenlose Probestunde <ArrowRight size={17} />
          </a>
        </nav>
      </header>

      <main id="top">
        <section className="hero section-pad">
          <div className="hero-copy">
            <div className="eyebrow"><Sparkles size={15} /> 1:1 Mathe-Nachhilfe · Klasse 6 bis Abitur</div>
            <h1>Mathe verstehen.<br /><span>Sicherer werden.</span></h1>
            <p className="hero-lead">Persönliche 1:1 Online-Mathenachhilfe – verständlich, strukturiert und ohne Druck.</p>
            <div className="hero-actions">
              <WhatsAppCta label="Kostenlose Probestunde per WhatsApp" />
              <a className="button button-primary" href="#booking" onClick={requestBookingOpen}>Freien Termin auswählen <CalendarCheck2 size={18} /></a>
            </div>
            {challengeEnabled && (
              <a className="hero-challenge-link" href="#challenge">
                Du hast gerade eine konkrete Aufgabe? Challenge senden <ArrowRight size={15} />
              </a>
            )}
            <div className="hero-trust">
              <span><Check size={16} /> Kostenlos & unverbindlich</span>
              <span><Check size={16} /> Persönlicher Kontakt</span>
              <span><Check size={16} /> Online & flexibel</span>
            </div>
          </div>

          <div className="hero-visual" aria-label="Beispiel einer verständlichen Mathe-Erklärung">
            <div className="math-board">
              <div className="board-top"><span /><span /><span /></div>
              <p className="board-label">Gemeinsam statt allein</p>
              <div className="equation">f(x) = x² − 4x + 3</div>
              <div className="board-step"><span>1</span> Nullstellen bestimmen</div>
              <div className="board-line"><i style={{ width: "78%" }} /></div>
              <div className="board-step"><span>2</span> Scheitelpunkt verstehen</div>
              <div className="mini-graph"><div className="axis-x" /><div className="axis-y" /><div className="parabola">⌣</div></div>
              <div className="board-success"><BadgeCheck size={20} /> Jetzt ergibt es Sinn!</div>
            </div>
            <div className="floating-card floating-time"><Clock3 size={20} /><span><strong>45 Minuten</strong><small>kostenlos kennenlernen</small></span></div>
            <div className="floating-card floating-grade"><GraduationCap size={20} /><span><strong>Klasse 6 bis Abitur</strong><small>passende Unterstützung
</small></span></div>
          </div>
        </section>

        <section className="proof-strip" aria-label="Vorteile">
          <div><strong>1:1</strong><span>persönliche Betreuung</span></div>
          <div><strong>45 min</strong><span>kostenlose Probestunde</span></div>
          <div><strong>Online</strong><span>flexibel von überall</span></div>
          <div><strong>Direkt</strong><span>an deinen Aufgaben</span></div>
        </section>

        <AboutJoeSection />

        <section className="content-section section-pad" id="so-lernst-du">
          <div className="section-heading centered">
            <div className="eyebrow">So lernst du</div>
            <h2>Keine Standardlösung.<br />Sondern Nachhilfe, die zu dir passt.</h2>
            <p>Wir arbeiten genau an dem, was dich gerade bremst – ruhig, verständlich und in deinem Tempo.</p>
          </div>
          <div className="feature-grid">
            <article className="feature-card accent-blue"><span className="feature-icon"><BookOpenCheck /></span><h3>Verstehen statt auswendig lernen</h3><p>Du erkennst Zusammenhänge und kannst sie selbst anwenden.</p></article>
            <article className="feature-card accent-green"><span className="feature-icon"><MessageCircleMore /></span><h3>Fragen ausdrücklich erlaubt</h3><p>Jeder Schritt darf nachgefragt werden – ohne Stress und Bewertung.</p></article>
            <article className="feature-card accent-orange"><span className="feature-icon"><CalendarCheck2 /></span><h3>Ruhiger in die Prüfung</h3><p>Wir üben passende Aufgaben und entwickeln klare Lösungsstrategien.</p></article>
          </div>
        </section>

        <PricingSection />

        <section className="process-section section-pad" id="ablauf">
          <div className="section-heading centered"><div className="eyebrow">Ganz unkompliziert</div><h2>So funktioniert die Probestunde.</h2></div>
          <div className="steps">
            <div className="step"><span>1</span><div><h3>Schreib mir oder such dir einen Termin aus</h3><p>Per WhatsApp oder direkt über meinen Kalender.</p></div></div>
            <div className="step"><span>2</span><div><h3>Wir lernen uns 45 Minuten kostenlos kennen</h3><p>Wir schauen gemeinsam, wo du stehst und wobei du Unterstützung brauchst.</p></div></div>
            <div className="step"><span>3</span><div><h3>Danach entscheidest du in Ruhe</h3><p>Du entscheidest ganz ohne Druck, ob du weitermachen möchtest.</p></div></div>
          </div>
        </section>

        <section className="booking-band section-pad" id="booking"><BookingSection /></section>

        <FaqSection />

        {challengeEnabled && (
          <section className="challenge-experience" id="challenge">
            <ChallengeGallery challenges={challengeState.challenges} />
            <div className="challenge-band section-pad"><ChallengeSection /></div>
          </section>
        )}

        <TestimonialsSection testimonials={testimonials} />

        <section className="final-cta section-pad">
          <div><span className="eyebrow light">Kostenlos kennenlernen</span><h2>Bereit, Mathe entspannter anzugehen?</h2><p>Die Probestunde ist kostenlos und unverbindlich.</p></div>
          <div className="final-actions"><WhatsAppCta label="Per WhatsApp schreiben" /><a className="button button-light" href="#booking" onClick={requestBookingOpen}>Freien Termin auswählen <ArrowRight /></a></div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="footer-brand"><span className="brand-mark">J</span><div><strong>Mathe mit Joe</strong><p>Persönliche Online-Nachhilfe von Klasse 6 bis zum Abitur.</p></div></div>
        <div className="footer-security"><ShieldCheck /><span>Deine Daten und Aufgaben werden vertraulich behandelt.</span></div>
        <nav aria-label="Rechtliches"><a href={viewUrl("impressum")}>Impressum</a><a href={viewUrl("datenschutz")}>Datenschutz</a></nav>
      </footer>
    </div>
  );
}
